import React, { useContext, useEffect, useState } from 'react';
import { FirebaseContext } from 'context';
import { Link, navigate, useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { LogoAbbreviated, Logo, NoAvatarPlus } from 'assets/svgs';

let fileReader;

if (typeof window !== 'undefined') {
  fileReader = new FileReader();
}

function Header({ setShowLogoutDialog }) {
  const { firebase, user } = useContext(FirebaseContext);
  const location = useLocation();
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [avatar, setAvatar] = useState(user?.avatarUrl || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [currentSessionSlug, setCurrentSessionSlug] = useState(null);
  const name = user?.name;

  const data = useStaticQuery(graphql`
    query {
      allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/(src/data/sessions)/" } }
        sort: { id: ASC }
      ) {
        nodes {
          frontmatter {
            id
            slug
            title
          }
        }
      }
    }
  `);

  const sessions = data.allMarkdownRemark.nodes
    .map((node) => node.frontmatter)
    .sort((a, b) => a.id.localeCompare(b.id));

  useEffect(() => {
    const match = location.pathname.match(/\/sessions\/([^/]+)/);
    if (match) {
      setCurrentSessionSlug(match[1]);
    } else {
      setCurrentSessionSlug(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setUserHasScrolled(true);
      } else {
        setUserHasScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    fileReader.addEventListener('load', () => {
      const _avatarFile = fileReader.result;
      setAvatarFile(_avatarFile);
    });
  }, []);

  useEffect(() => {
    if (avatarFile) {
      /* Show the loading spinner for 1 second before then setting the local file in memory as the avatar first, which is then replaced by the resized version at the same time as all the user comment images are updated. */
      setSavingAvatar(true);
      setTimeout(() => {
        /* We set the local file in memory as the avatar here first, as the file is still being
        resized and uploaded to the database for persistence. */
        setAvatar(avatarFile);
        setSavingAvatar(false);
      }, 1000);

      firebase.userProfile
        .uploadAvatar(avatarFile)
        .then(async () => firebase.auth.currentUser.reload());
    }
  }, [avatarFile]);

  useEffect(() => {
    if (user && user.avatarUrl !== avatar) {
      setAvatar(user.avatarUrl);
    }
  }, [user?.avatarUrl]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (showSessionDropdown) {
        setShowSessionDropdown(false);
      }
    };

    if (showSessionDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSessionDropdown]);

  const handleShowLogoutDialog = (e) => {
    e.stopPropagation();
    setShowLogoutDialog(true);
  };

  const handleSessionChange = (slug) => {
    navigate(`/sessions/${slug}`);
    setShowSessionDropdown(false);
  };

  const currentSessionIndex = sessions.findIndex(
    (session) => session.slug === currentSessionSlug
  );

  const shouldShowSessionDropdown =
    user.email.endsWith('@agencyx.ie') &&
    sessions.length > 1 &&
    currentSessionSlug;

  return (
    <AnimatePresence>
      {name && (
        <Container $userHasScrolled={userHasScrolled}>
          <StyledLogoLink to="/">
            <StyledLogoAbbreviated $userHasScrolled={userHasScrolled} />
          </StyledLogoLink>
          {shouldShowSessionDropdown && (
            <SessionDropdownWrapper>
              <SessionDropdownButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSessionDropdown(!showSessionDropdown);
                }}
                $userHasScrolled={userHasScrolled}>
                Session {currentSessionIndex + 1}{' '}
                {sessions[currentSessionIndex]?.id === 'hl1t9c'
                  ? '(Ireland)'
                  : '(Switzerland)'}
                <DropdownArrow $isOpen={showSessionDropdown}>▼</DropdownArrow>
              </SessionDropdownButton>
              {showSessionDropdown && (
                <SessionDropdownMenu onClick={(e) => e.stopPropagation()}>
                  {sessions.map((session, index) => (
                    <SessionDropdownItem
                      key={session.id}
                      $isActive={session.slug === currentSessionSlug}
                      onClick={() => handleSessionChange(session.slug)}>
                      Session {index + 1} (
                      {session.id === 'hl1t9c' ? 'Ireland' : 'Switzerland'})
                    </SessionDropdownItem>
                  ))}
                </SessionDropdownMenu>
              )}
            </SessionDropdownWrapper>
          )}
          <HeaderButtonContainer $userHasScrolled={userHasScrolled}>
            <InputLabel htmlFor="avatarFile">
              {savingAvatar ? (
                <LoadingSpinner className="loading-spinner" />
              ) : user?.avatarUrl || avatarFile ? (
                <Avatar src={avatar} alt={user?.name} />
              ) : (
                <NoAvatarPlus />
              )}
            </InputLabel>
            <NameButton
              onClick={handleShowLogoutDialog}
              $userHasScrolled={userHasScrolled}>
              {name}
            </NameButton>
          </HeaderButtonContainer>
          <HiddenFileUpload
            type="file"
            name="avatarFile"
            id="avatarFile"
            disabled={savingAvatar}
            onChange={(e) =>
              e.target.files.length &&
              fileReader.readAsDataURL(e.target.files[0])
            }
          />
        </Container>
      )}
    </AnimatePresence>
  );
}

const InputLabel = styled.label`
  cursor: pointer;
  display: contents;
`;

export const HiddenFileUpload = styled.input`
  height: 1px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  user-select: none;
  width: 1px;
`;

const Container = styled(motion.header).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  }
})`
  align-items: center;
  background-color: ${({ $userHasScrolled }) =>
    $userHasScrolled ? 'rgba(211,227,253, 0.35)' : 'transparent'};
  backdrop-filter: ${({ $userHasScrolled }) =>
    $userHasScrolled ? 'blur(0.9375rem)' : 'blur(0rem)'};
  display: flex;
  justify-content: space-between;
  max-width: 191rem;
  margin: 0 auto;
  padding: ${({ $userHasScrolled }) =>
    $userHasScrolled ? '0.75rem 1.25rem 0.75rem' : '1.2rem 1.25rem 0.75rem'};
  position: fixed;
  top: 0;
  transition:
    background-color 0.3s ease-in-out,
    padding 0.3s ease-in-out;
  width: 100%;
  z-index: 3;

  @media (min-width: 1000px) {
    padding: ${({ $userHasScrolled }) =>
      $userHasScrolled
        ? '0.75rem 1.25rem 0.75rem'
        : '3.813rem 1.25rem 0.75rem'};
    transition:
      background-color 0.5s ease-in-out,
      padding 0.5s ease-in-out;
  }

  @media (min-width: 1440px) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 1.25rem;
  }
`;

const StyledLogoLink = styled(Link)`
  flex-shrink: 1;
  grid-column: 2/4;
  position: relative;
  top: 4px;
`;

const StyledLogoAbbreviated = styled(LogoAbbreviated)`
  max-width: 6rem;
  width: 100%;

  path {
    fill: ${({ $userHasScrolled }) => ($userHasScrolled ? 'default' : '#fff')};
    transition: fill 0.5s ease-in-out;
  }

  @media (min-width: 1000px) {
    transition: max-width 0.5s ease-in-out;
    max-width: ${({ $userHasScrolled }) =>
      $userHasScrolled ? '6rem' : '8.938rem'};
  }
`;

const Avatar = styled(motion.img).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
})`
  background: transparent;
  border-radius: 50%;
  object-fit: cover;
`;

const HeaderButtonContainer = styled.div`
  align-items: center;
  background-color: rgba(0, 123, 255, 0.7);
  border: none;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-weight: 500;
  justify-content: flex-start;
  padding: 0.3125em 1.25em 0.3125em 0.35em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    outline: 0.063em solid rgba(0, 123, 255, 0.7);
    outline-offset: 0.125em;
    filter: brightness(1.225);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  svg,
  img,
  .loading-spinner {
    height: 33px;
    width: 33px;
  }

  @media (min-width: 768px) {
    margin-left: 34px;
  }

  @media (min-width: 1000px) {
    padding: ${({ $userHasScrolled }) =>
      $userHasScrolled
        ? '0.3125em 1.25em 0.3125em 0.35em'
        : '0.45em 2.4em 0.4em 0.5em'};
    transition: padding 0.5s ease-in-out;
    white-space: nowrap;
    width: fit-content;

    svg,
    img,
    .loading-spinner {
      height: ${({ $userHasScrolled }) => ($userHasScrolled ? '30px' : '46px')};
      width: ${({ $userHasScrolled }) => ($userHasScrolled ? '30px' : '46px')};
      transition:
        width 0.5s ease-in-out,
        height 0.5s ease-in-out;
    }
  }

  @media (min-width: 1440px) {
    grid-column: 10/12;
  }
`;

const NameButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  margin-left: ${({ $userHasScrolled }) =>
    $userHasScrolled ? '0.45em' : '0.65em'};
  outline: none;
  padding: 0;
  transition: margin-left 0.5s ease-in-out;

  @media (min-width: 1000px) {
    margin-left: ${({ $userHasScrolled }) =>
      $userHasScrolled ? '0.45em' : '0.65em'};
  }
`;

const LoadingSpinner = styled.span`
  animation: spin 0.8s linear infinite;
  border: 2px solid #fff;
  border-radius: 50%;
  border-top-color: transparent;
  scale: 0.75;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SessionDropdownWrapper = styled.div`
  display: none;
  position: relative;
  z-index: 4;

  @media (min-width: 768px) {
    display: block;
    width: fit-content;
    min-width: 13.25rem;
    margin: 0 auto;
  }

  @media (min-width: 1440px) {
    grid-column: 6/8;
  }
`;

const SessionDropdownButton = styled.button`
  align-items: center;
  background-color: rgba(0, 123, 255, 0.7);
  border: none;
  border-radius: var(--border-radius-lg);
  color: #fff;
  cursor: pointer;
  display: flex;
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  gap: 0.5rem;
  justify-content: space-between;
  outline: none;
  padding: 0.625rem 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  width: 100%;

  &:hover,
  &:focus-visible {
    filter: brightness(1.225);
    outline: 0.063em solid rgba(0, 123, 255, 0.7);
    outline-offset: 0.125em;
  }

  &:active {
    transform: scale(0.97);
  }

  @media (min-width: 1000px) {
    font-size: 1rem;
    padding: ${({ $userHasScrolled }) =>
      $userHasScrolled ? '0.625rem 1rem' : '0.75rem 1.25rem'};
    transition:
      all 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      padding 0.5s ease-in-out;
  }
`;

const DropdownArrow = styled.span`
  font-size: 0.625rem;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out;
`;

const SessionDropdownMenu = styled.div`
  background-color: #fff;
  border: 1px solid rgba(0, 123, 255, 0.3);
  /* border-radius: var(--border-radius-lg); */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
  position: absolute;
  width: 100%;
  z-index: 5;
`;

const SessionDropdownItem = styled.button`
  background-color: ${({ $isActive }) =>
    $isActive ? 'rgba(0, 123, 255, 0.1)' : 'transparent'};
  border: none;
  color: var(--blue);
  cursor: pointer;
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '400')};
  outline: none;
  padding: 0.75rem 1rem;
  text-align: left;
  transition: background-color 0.2s ease-in-out;
  width: 100%;

  &:hover {
    background-color: rgba(0, 123, 255, 0.15);
  }

  &:active {
    background-color: rgba(0, 123, 255, 0.25);
  }

  @media (min-width: 1000px) {
    font-size: 1rem;
  }
`;

export default Header;
